import { useState, useMemo, useEffect } from 'react';
import { SavedState } from '@/components/team-details/types/selector-types';

export const useTeamSeasons = (teamName: string | undefined, savedStates: any[] | null) => {
  const [selectedSeasons, setSelectedSeasons] = useState<string[]>([]);

  const availableSeasonsWithLeagues = useMemo(() => {
    if (!savedStates || !teamName) return [];

    const seasonMap = new Map<string, Set<string>>();

    savedStates.forEach(state => {
      const hasTeam = Array.isArray(state.data) && state.data.some((match: any) =>
        match.HomeTeam === teamName || match.AwayTeam === teamName
      );

      if (hasTeam && state.year) {
        if (!seasonMap.has(state.year)) {
          seasonMap.set(state.year, new Set());
        }
        if (state.league) {
          seasonMap.get(state.year)?.add(state.league);
        }
      }
    });

    return Array.from(seasonMap.entries())
      .map(([season, leagues]) => ({
        season,
        league: Array.from(leagues)[0],
        country: savedStates.find(state => 
          state.year === season && 
          state.league === Array.from(leagues)[0]
        )?.country || ''
      }))
      .sort((a, b) => {
        // Sort seasons in descending order (newest first)
        const yearA = parseInt(a.season.split('/')[0]);
        const yearB = parseInt(b.season.split('/')[0]);
        return yearB - yearA;
      });
  }, [teamName, savedStates]);

  useEffect(() => {
    // Only set initial season if we have available seasons and no seasons are currently selected
    if (availableSeasonsWithLeagues.length > 0 && selectedSeasons.length === 0) {
      const newestSeason = availableSeasonsWithLeagues[0].season;
      console.log('Setting initial season:', newestSeason);
      setSelectedSeasons([newestSeason]);
    }
  }, [availableSeasonsWithLeagues]);

  const handleSeasonToggle = (season: string) => {
    setSelectedSeasons(prev => {
      if (prev.includes(season)) {
        // If removing the last season, keep it selected
        if (prev.length === 1) return prev;
        return prev.filter(s => s !== season);
      }
      const newSeasons = [...prev, season];
      // Sort selected seasons in descending order (newest first)
      return newSeasons.sort((a, b) => {
        const yearA = parseInt(a.split('/')[0]);
        const yearB = parseInt(b.split('/')[0]);
        return yearB - yearA;
      });
    });
  };

  return {
    selectedSeasons,
    setSelectedSeasons,
    availableSeasonsWithLeagues,
    handleSeasonToggle
  };
};
